import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChartBar } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CapacityPage({ user }) {
  const [capacityData, setCapacityData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCapacity();
  }, []);

  const fetchCapacity = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const response = await axios.get(`${API}/locations/capacity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCapacityData(response.data);
    } catch (error) {
      toast.error('Failed to load capacity data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  const getUtilizationColor = (utilization) => {
    if (utilization > 80) return 'bg-[#DC2626]';
    if (utilization > 50) return 'bg-[#F59E0B]';
    return 'bg-[#16A34A]';
  };

  const getUtilizationBg = (utilization) => {
    if (utilization > 80) return 'bg-[#DC2626]/10 border-[#DC2626]/20';
    if (utilization > 50) return 'bg-[#F59E0B]/10 border-[#F59E0B]/20';
    return 'bg-[#16A34A]/10 border-[#16A34A]/20';
  };

  return (
    <div className="p-8" data-testid="capacity-page">
      <div className="mb-8">
        <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="capacity-title">
          Warehouse Capacity
        </h1>
        <p className="text-[#52525B]">Monitor storage utilization across all zones</p>
      </div>

      {!capacityData || Object.keys(capacityData).length === 0 ? (
        <div className="card text-center py-12">
          <ChartBar size={48} className="mx-auto text-[#52525B] mb-4" weight="duotone" />
          <h3 className="heading text-xl font-bold text-[#09090B] mb-2">No capacity data</h3>
          <p className="text-[#52525B]">Add locations to see capacity information</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(capacityData).map(([zoneName, zoneData]) => (
            <div key={zoneName} className={`card ${getUtilizationBg(zoneData.utilization)}`} data-testid="zone-card">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="heading text-xl font-bold text-[#09090B]" data-testid="zone-name">{zoneName}</h3>
                  <span className="heading text-2xl font-black text-[#09090B]" data-testid="zone-utilization">
                    {zoneData.utilization.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-[#E4E4E7] rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${getUtilizationColor(zoneData.utilization)}`}
                    style={{ width: `${zoneData.utilization}%` }}
                    data-testid="zone-utilization-bar"
                  ></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-[#52525B]">Total Capacity</p>
                  <p className="mono text-lg font-bold text-[#09090B]" data-testid="zone-total-capacity">{zoneData.total_capacity}</p>
                </div>
                <div>
                  <p className="text-sm text-[#52525B]">Used</p>
                  <p className="mono text-lg font-bold text-[#09090B]" data-testid="zone-used-capacity">{zoneData.used_capacity}</p>
                </div>
              </div>

              <details className="group">
                <summary className="cursor-pointer text-sm font-medium text-[#007AFF] hover:text-[#0051D5] list-none flex items-center justify-between" data-testid="zone-locations-toggle">
                  <span>View Locations ({zoneData.locations.length})</span>
                  <span className="transform group-open:rotate-180 transition-transform duration-150">▼</span>
                </summary>
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {zoneData.locations.map((loc) => {
                    const locUtil = (loc.current_qty / loc.capacity * 100) || 0;
                    return (
                      <div key={loc.id} className="flex items-center justify-between p-2 bg-white rounded border border-[#E4E4E7]" data-testid="location-item">
                        <div className="flex-1">
                          <p className="mono text-sm font-bold text-[#09090B]" data-testid="location-code">{loc.code}</p>
                          <p className="text-xs text-[#52525B] capitalize" data-testid="location-type">{loc.location_type}</p>
                        </div>
                        <div className="text-right">
                          <p className="mono text-sm font-bold text-[#09090B]" data-testid="location-quantity">{loc.current_qty}/{loc.capacity}</p>
                          <p className="text-xs text-[#52525B]" data-testid="location-utilization">{locUtil.toFixed(0)}%</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
